import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { commandsFor, filterCommands, isTypingTarget, matchesShortcut, SHORTCUTS } from '@/app/palette'
import { hitPath, MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_MS, searchCrm, useDebounced } from '@/app/search'

/**
 * Палитра команд, Ctrl+K (ТЗ §5.1), и глобальный поиск по CRM (#127).
 *
 * Открывается поверх всего, закрывается по Escape и по клику вне. Сверху — разделы, на
 * которые у пользователя есть права (тот же источник, что у меню), ниже — найденные записи.
 * Поиск уходит на сервер после паузы в наборе; что из найденного показать, решает сервер
 * областью видимости списков, а не палитра.
 */
/** Событие, которым палитру открывает не клавиатура, а кнопка поиска в верхней панели. */
const OPEN_EVENT = 'crm:open-palette'

export function openPalette(): void {
  window.dispatchEvent(new Event(OPEN_EVENT))
}

/**
 * Состояния, которые палитра называет значком: то, ради чего ищут («просрочен», «оплачен»).
 * Рабочие статусы («отправлен», «в работе») значком не выводятся — на каждой строке они
 * перестают читаться.
 */
const BADGE_STATES = new Set(['overdue', 'paid', 'cancelled', 'won', 'lost'])

/** Строка палитры: раздел или найденная запись — Enter делает одно и то же, переход. */
interface Entry {
  id: string
  label: string
  path: string
  group: 'sections' | 'records'
  kind?: string
  detail?: string | null
  state?: string | null
}

export function CommandPalette() {
  const { user, locale, t } = useApp()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const commands = useMemo(() => commandsFor(user, locale), [user, locale])
  const matches = useMemo(() => filterCommands(commands, query), [commands, query])

  const settled = useDebounced(query.trim(), SEARCH_DEBOUNCE_MS)
  const searching = open && settled.length >= MIN_SEARCH_LENGTH
  const found = useQuery({
    queryKey: ['search', settled],
    queryFn: ({ signal }) => searchCrm(settled, signal),
    enabled: searching,
    staleTime: 30_000,
    retry: false,
  })

  const entries = useMemo<Entry[]>(() => {
    const sections: Entry[] = matches.map((command) => ({
      id: `cmd:${command.id}`, label: command.label, path: command.path, group: 'sections',
    }))
    // Ответ на прежний запрос не показывается под новым вводом: иначе Enter открыл бы
    // запись, найденную по тексту, которого в поле уже нет.
    const current = searching && found.data && settled === query.trim() ? found.data.items : []
    const records: Entry[] = []
    for (const hit of current) {
      const path = hitPath(hit, user)
      if (!path) continue
      records.push({
        id: `hit:${hit.type}:${hit.id}`,
        label: hit.title,
        path,
        group: 'records',
        kind: hit.type,
        detail: hit.fragment || hit.subtitle,
        state: hit.state && BADGE_STATES.has(hit.state) ? hit.state : null,
      })
    }
    return [...sections, ...records]
  }, [matches, found.data, searching, settled, query, user])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // В поле ввода горячая клавиша — это символ. Без проверки нельзя написать
      // дробь в комментарии: фокус уедет в палитру.
      if (!open && isTypingTarget(event.target)) return

      if (matchesShortcut(event, SHORTCUTS.palette!)) {
        event.preventDefault()
        setOpen((wasOpen) => !wasOpen)
        setQuery('')
        setSelected(0)
        return
      }
      if (open && event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  useEffect(() => {
    function onOpen() {
      setOpen(true)
      setQuery('')
      setSelected(0)
    }
    window.addEventListener(OPEN_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_EVENT, onOpen)
  }, [])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  // Выбранная строка сбрасывается при смене запроса: иначе после сужения списка
  // подсветка остаётся на позиции, которой больше нет, и Enter открывает не то.
  useEffect(() => setSelected(0), [query])

  if (!open) return null

  function run(path: string) {
    setOpen(false)
    navigate(path)
  }

  function onInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setSelected((index) => Math.min(index + 1, entries.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setSelected((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const entry = entries[selected]
      if (entry) run(entry.path)
    }
  }

  const pending = searching && (found.isFetching || settled !== query.trim())
  const sectionCount = entries.filter((entry) => entry.group === 'sections').length

  return (
    <div className="palette-overlay" onClick={() => setOpen(false)} role="presentation">
      <div
        className="palette"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.placeholder')}
      >
        <input
          ref={inputRef}
          className="palette-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onInputKeyDown}
          placeholder={t('palette.placeholder')}
          aria-autocomplete="list"
        />
        {entries.length === 0 ? (
          <p className="palette-empty">
            {pending ? t('palette.searching') : found.isError ? t('palette.searchFailed') : t('palette.empty')}
          </p>
        ) : (
          <ul className="palette-list" role="listbox">
            {entries.map((entry, index) => (
              <li key={entry.id}>
                {index === sectionCount && entry.group === 'records' ? (
                  <div className="palette-group" role="presentation">{t('palette.records')}</div>
                ) : null}
                <button
                  type="button"
                  className={index === selected ? 'palette-item is-selected' : 'palette-item'}
                  onClick={() => run(entry.path)}
                  onMouseEnter={() => setSelected(index)}
                  role="option"
                  aria-selected={index === selected}
                >
                  {entry.group === 'records' ? (
                    <span className="palette-record">
                      <span className="palette-record-head">
                        <span className="palette-kind">{t(`search.type.${entry.kind}`)}</span>
                        <span className="palette-title">{entry.label}</span>
                        {entry.state ? (
                          <span className="palette-state">{t(`search.state.${entry.state}`)}</span>
                        ) : null}
                      </span>
                      {entry.detail ? <span className="palette-detail">{entry.detail}</span> : null}
                    </span>
                  ) : (
                    entry.label
                  )}
                </button>
              </li>
            ))}
            {pending ? <li className="palette-pending">{t('palette.searching')}</li> : null}
          </ul>
        )}
      </div>
    </div>
  )
}
